import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaUndo, FaExpandArrowsAlt, FaTimes, FaArrowLeft } from 'react-icons/fa';
import './ChatWindow.css'; // Optional: for custom chat styles
import { API_ENDPOINTS, backendConfig } from '../config/backendConfig';
import { enqueueAndPoll } from '../utils/jobPolling';
import { saveReimagineActivity, checkExistingActivity } from '../utils/activityService';
import { checkContentModeration, getContentModerationMessage } from '../utils/contentModeration';
import { useAuth } from '../context/AuthContext';
import KidBackground from '../ui/backgrounds/KidBackground';
import splashImage from '../assets/splash.png';
import ContentModerationModal from '../components/ContentModerationModal';
import { useFeatureQuota } from '../hooks/useFeatureQuota';
import UpgradeQuotaModal from '../components/UpgradeQuotaModal';

export default function ReimagineStudio({ externalState, hideChrome } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { checkQuotaBeforeUse, recordUsage, showUpgradeModal, dismissUpgradeModal, upgradeModalFeature } = useFeatureQuota();
  // Accept query params fallback to allow iframe overlay to load same UI
  const searchParams = new URLSearchParams(location.search || '');
  const qsImage = searchParams.get('imageUrl');
  const qsTitle = searchParams.get('title');
  const qsTechnique = searchParams.get('technique');
  const qsInspiration = searchParams.get('inspiration');

  const state = externalState || location.state || {};
  const initialArtworkUrl =
    state?.artwork?.originalArtwork ||
    state?.artwork?.imageUrl ||
    state?.artwork?.coverUrl ||
    state?.imageUrl ||
    qsImage ||
    'https://dummyimage.com/400x400/eee/aaa&text=Your+Artwork+Here';

  const [imageHistory, setImageHistory] = useState([initialArtworkUrl]);
  const [chatInput, setChatInput] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [promptHistory, setPromptHistory] = useState([]);
  
  // Store the original image URL separately to ensure consistent grouping
  const [originalImageUrl] = useState(() => {
    return state?.artwork?.imageUrl || initialArtworkUrl;
  });
  // Track the last saved reimagine activity so feedback can always
  // update the SAME card, even if lookup logic ever fails.
  const [lastReimagineActivityId, setLastReimagineActivityId] = useState(null);
  const [showImpactQuestion, setShowImpactQuestion] = useState(false);
  const [impactInput, setImpactInput] = useState("");
  const [appreciation, setAppreciation] = useState(null);
  const [appreciationLoading, setAppreciationLoading] = useState(false);
  /** Reimagine impact card: front = feedback, back = mentor exploration (Try next). */
  const [impactExploreFlipped, setImpactExploreFlipped] = useState(false);
  const [imageUpdating, setImageUpdating] = useState(false);
  const [artworkLoaded, setArtworkLoaded] = useState(false);

  // Content moderation modal (same UX as Storyboarding / SceneEdit)
  const [showContentModerationModal, setShowContentModerationModal] = useState(false);
  const [contentModerationMessage, setContentModerationMessage] = useState('');
  const [contentModerationKeywords, setContentModerationKeywords] = useState([]);

  const currentImageUrl = imageHistory[imageHistory.length - 1];

  // On mount, check if there is already a saved reimagine card for this
  // artwork. If yes, remember its ID so ALL future saves (auto‑save +
  // feedback) target that SAME document and never create a duplicate.
  useEffect(() => {
    let cancelled = false;
    async function preloadExistingReimagine() {
      try {
        if (!originalImageUrl) return;
        const existing = await checkExistingActivity(originalImageUrl, 'reimagine');
        if (!cancelled && existing?.id) {
          console.log('🎯 Preloaded existing reimagine activity ID for this artwork:', existing.id);
          setLastReimagineActivityId(existing.id);
        }
      } catch (e) {
        console.warn('⚠️ Failed to preload existing reimagine activity:', e?.message || e);
      }
    }
    preloadExistingReimagine();
    return () => {
      cancelled = true;
    };
  }, [originalImageUrl]);

  useEffect(() => {
    setImpactExploreFlipped(false);
  }, [appreciation]);

  // Handle chat send (reimagine prompt)
  async function handleChatSend(e) {
    e.preventDefault();
    if (!chatInput.trim()) return;

    // FRONTEND content moderation (instant feedback before calling backend)
    const moderationCheck = checkContentModeration(chatInput);
    if (moderationCheck.isInappropriate) {
      const msg = getContentModerationMessage('reimagine', moderationCheck.matchedKeywords);
      setContentModerationMessage(msg);
      setContentModerationKeywords(moderationCheck.matchedKeywords || []);
      setShowContentModerationModal(true);
      return;
    }
    const quotaCheck = await checkQuotaBeforeUse('reimagineStudio');
    if (!quotaCheck.allowed) {
      if (quotaCheck.overLimit) return;
      return;
    }
    await recordUsage('reimagineStudio');
    setChatLoading(true);
    setImageUpdating(true);
    setShowImpactQuestion(false);
    setAppreciation(null);
    setChatHistory(prev => [...prev, { role: 'user', text: chatInput }]);
    setPromptHistory(prev => [...prev, chatInput]);
    // Helper: ensure we always send a base64 data URL to backend
    async function ensureDataUrl(url) {
      try {
        if (typeof url !== 'string') return url;
        if (url.startsWith('data:image/')) return url; // already base64
        
        // For other URLs, try to fetch and convert
        const resp = await fetch(url, {
          mode: 'cors',
          headers: {
            'Accept': 'image/*'
          }
        });
        
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
        }
        
        const blob = await resp.blob();
        return await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (err) {
        console.warn('Failed to convert image to data URL, sending raw URL instead:', err?.message || err);
        return url;
      }
    }

    try {
      console.log('🚀 Making reimagine API call to:', API_ENDPOINTS.reimagineArtworkFirebase || API_ENDPOINTS.reimagineArtwork);
      const preparedImageUrl = await ensureDataUrl(currentImageUrl);
      console.log('📤 Request payload:', { imageUrl: preparedImageUrl, prompt: chatInput });
      
      // Use background worker with polling
      const data = await enqueueAndPoll(
        API_ENDPOINTS.reimagineArtworkFirebase || API_ENDPOINTS.reimagineArtwork,
        { imageUrl: preparedImageUrl, prompt: chatInput },
        {
          interval: 2000, // Poll every 2 seconds
          maxAttempts: 150, // 5 minutes max
          onProgress: (status, job, attempts) => {
            setChatLoading(true);
            setImageUpdating(true);
          },
          onStatusChange: (status) => {
            console.log('🔄 Job status changed:', status);
          },
          baseUrl: backendConfig.baseUrl
        }
      );

      console.log('📥 Response data received, has generatedImageUrl:', !!data.generatedImageUrl);
      console.log('📥 Response data keys:', Object.keys(data || {}));
      if (data.generatedImageUrl) {
        console.log('✅ Generated image URL length:', data.generatedImageUrl.length);
        
        // Update state immediately - don't wait for preload
        setImageHistory(prev => [...prev, data.generatedImageUrl]);
        setChatHistory(prev => [
          ...prev,
          { role: 'ai', text: 'Here is your reimagined artwork!', imageUrl: data.generatedImageUrl }
        ]);
        setShowImpactQuestion(true);
        console.log('✅ Reimagine transformation completed, waiting for feedback');
        setChatLoading(false);
        setImageUpdating(false);
        
        // Preload the image in the background (non-blocking)
        const img = new Image();
        img.onload = () => {
          console.log('✅ Generated image preloaded successfully');
        };
        img.onerror = (err) => {
          console.warn('⚠️ Generated image failed to preload:', err);
        };
        img.src = data.generatedImageUrl;

        // AUTO‑SAVE this transformation immediately into the reimagine activity card.
        // Each reimagine run becomes a new transformation inside the same card
        // (grouped by originalImageUrl + activity type).
        try {
          const nextTransformationCount = imageHistory.length + 1;
          const classNameFromState = state?.artwork?.className || (typeof window !== 'undefined' && localStorage.getItem('mentorClass')) || null;
          const sectionFromState = state?.artwork?.section || (typeof window !== 'undefined' && localStorage.getItem('mentorSection')) || null;
          let artistNameFromState = state?.artwork?.artist || null;
          try {
            if (!artistNameFromState && typeof window !== 'undefined') {
              const sel = localStorage.getItem('mentorSelectedArtwork');
              if (sel) artistNameFromState = JSON.parse(sel)?.artist || null;
            }
          } catch (_) {}

          console.log('🔄 Auto‑saving reimagine transformation (no feedback yet):', {
            originalImageUrl,
            transformedImageUrl: data.generatedImageUrl,
            prompt: chatInput,
            transformationCount: nextTransformationCount
          });

          const savedId = await saveReimagineActivity({
            originalImageUrl: originalImageUrl,
            transformedImageUrl: data.generatedImageUrl,
            title: 'Artwork Reimagined',
            description: `Artwork transformed with prompt: ${chatInput}`,
            metadata: {
              prompt: chatInput,
              transformationCount: nextTransformationCount,
              feature: 'reimagine',
              // If we already know which activity to update (from preload),
              // pass it through so we NEVER create a new card for this artwork.
              activityIdForUpdate: lastReimagineActivityId || undefined,
              context: (user && (user.accountType === 'mentor' || user.role === 'mentor')) ? 'mentor' : 'student',
              mentorId: (user && (user.accountType === 'mentor' || user.role === 'mentor')) ? user.uid : null,
              className: classNameFromState || undefined,
              section: sectionFromState || undefined,
              artistName: artistNameFromState || undefined
            }
          }, true);

          // Remember which activity document was saved so that when
          // feedback is generated we can force an update on the SAME
          // card instead of ever creating a duplicate.
          if (savedId) {
            setLastReimagineActivityId(savedId);
          }
        } catch (autoSaveError) {
          console.error('❌ Failed to auto‑save reimagine transformation:', autoSaveError);
        }
      } else {
        console.warn('⚠️ Response missing generatedImageUrl, error:', data.error);
        const errMsg = data.error || 'Sorry, I could not generate an image for that prompt.';
        setChatHistory(prev => [...prev, { role: 'ai', text: errMsg }]);
        setChatLoading(false);
        setImageUpdating(false);
      }
    } catch (err) {
      console.error('❌ Reimagine API call failed:', err);
      console.error('❌ Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });

      // If backend content moderation triggers, err.message contains the
      // kid‑friendly message from getContentModerationMessage('reimagine', ...)
      if (err.message && err.message.toLowerCase().includes('artmosphere norms')) {
        setContentModerationMessage(err.message);
        setContentModerationKeywords([]);
        setShowContentModerationModal(true);
      } else {
        const friendly =
          err.name === 'AbortError'
            ? 'The request timed out. Please try again.'
            : `Error: ${err.message}. Please try again.`;
        setChatHistory(prev => [
          ...prev,
          { role: 'ai', text: friendly }
        ]);
      }
    } finally {
      setChatInput("");
      setChatLoading(false);
      setImageUpdating(false);
    }
  }

  // Handle impact question submit
  async function handleImpactSubmit(e) {
    e.preventDefault();
    if (!impactInput.trim()) return;

    // FRONTEND content moderation for impact text + last prompt
    const textToCheck = `${impactInput || ''} ${promptHistory[promptHistory.length - 1] || ''}`;
    const moderationCheck = checkContentModeration(textToCheck);
    if (moderationCheck.isInappropriate) {
      const msg = getContentModerationMessage('reimagine appreciation', moderationCheck.matchedKeywords);
      setContentModerationMessage(msg);
      setContentModerationKeywords(moderationCheck.matchedKeywords || []);
      setShowContentModerationModal(true);
      return;
    }
    setAppreciationLoading(true);
    setChatHistory(prev => [
      ...prev,
      { role: 'user', text: impactInput }
    ]);
    try {
      // Find the last reimagine prompt (from chatHistory or promptHistory)
      let reimaginePrompt = '';
      if (promptHistory && promptHistory.length > 0) {
        reimaginePrompt = promptHistory[promptHistory.length - 1];
      } else if (chatHistory && chatHistory.length > 0) {
        // fallback: last user message
        const lastUserMsg = [...chatHistory].reverse().find(msg => msg.role === 'user');
        if (lastUserMsg) reimaginePrompt = lastUserMsg.text;
      }
      console.log('🟡 Submitting impact feedback to API (using background job processing):', {
        endpoint: API_ENDPOINTS.reimagineAppreciation,
        hasImage: !!currentImageUrl,
        impactInput,
        reimaginePrompt
      });
      
      // Use background job processing (like style-transfer and reimagine) to reduce costs
      const jobResult = await enqueueAndPoll(
        API_ENDPOINTS.reimagineAppreciation,
        {
          imageUrl: currentImageUrl,
          userImpactText: impactInput,
          reimaginePrompt
        },
        {
          interval: 1000, // Poll every 1 second (appreciation is faster)
          maxAttempts: 30, // Max 30 seconds
          onProgress: (status) => {
            if (status.status === 'processing') {
              console.log('🔄 Appreciation generation in progress...');
            }
          }
        }
      );
      
      console.log('🟢 Appreciation job completed');
      console.log('🟢 Job result structure:', jobResult);
      
      // enqueueAndPoll returns the result from the worker
      // Worker returns: { success: true, appreciation }
      // enqueueAndPoll extracts status.result, so jobResult should be { success: true, appreciation }
      let appreciationData = null;
      
      if (jobResult.appreciation) {
        // Direct appreciation array
        appreciationData = jobResult.appreciation;
      } else if (jobResult.result?.appreciation) {
        // Nested in result field
        appreciationData = jobResult.result.appreciation;
      } else if (jobResult.result && Array.isArray(jobResult.result)) {
        // Result is the appreciation array directly
        appreciationData = jobResult.result;
      } else if (Array.isArray(jobResult)) {
        // Result is the appreciation array directly
        appreciationData = jobResult;
      }
      
      if (!appreciationData || !Array.isArray(appreciationData)) {
        console.error('❌ No appreciation data in job result:', jobResult);
        throw new Error('No appreciation data in job result');
      }
      
      const data = {
        appreciation: appreciationData
      };
      
      console.log('🟢 Impact API json:', data);
      if (data.appreciation && Array.isArray(data.appreciation)) {
        setAppreciation(data.appreciation);
        setChatHistory(prev => [
          ...prev,
          { role: 'ai', text: 'Here is some art appreciation for your updated artwork:', appreciation: data.appreciation }
        ]);
        
        // Auto-save reimagine activity with feedback to dashboard
        // This is the ONLY place we save reimagine activities to prevent duplicates
        try {
          const lastPrompt = promptHistory[promptHistory.length - 1];
          const lastTransformedImage = imageHistory[imageHistory.length - 1];
          
          console.log('🔄 Saving reimagine activity with feedback (single save point):', {
            originalImageUrl: originalImageUrl,
            transformedImageUrl: lastTransformedImage,
            prompt: lastPrompt,
            userFeedback: impactInput,
            artmosphereFeedback: data.appreciation
          });
          
          const classNameFromState = state?.artwork?.className || (typeof window !== 'undefined' && localStorage.getItem('mentorClass')) || null;
          const sectionFromState = state?.artwork?.section || (typeof window !== 'undefined' && localStorage.getItem('mentorSection')) || null;
          let artistNameFromState = state?.artwork?.artist || null;
          try {
            if (!artistNameFromState && typeof window !== 'undefined') {
              const sel = localStorage.getItem('mentorSelectedArtwork');
              if (sel) artistNameFromState = JSON.parse(sel)?.artist || null;
            }
          } catch (_) {}
          
          // Save reimagine activity - this will update existing if found, or create new
          // The appreciation feedback is saved as part of the reimagine activity metadata
          // This ensures grouping: all activities for the same artwork are in one card
          const savedActivity = await saveReimagineActivity({
            originalImageUrl: originalImageUrl, // Original image
            transformedImageUrl: lastTransformedImage,
            title: 'Artwork Reimagined',
            description: `Artwork transformed with prompt: ${lastPrompt}`,
            metadata: {
              prompt: lastPrompt,
              transformationCount: imageHistory.length,
              userFeedback: impactInput,
              artmosphereFeedback: data.appreciation,
              feature: 'reimagine-with-feedback',
              // Force the feedback save to update the SAME activity card
              // that was created/updated during auto‑save, even if any
              // lookup logic ever fails. This prevents duplicate cards.
              activityIdForUpdate: lastReimagineActivityId || undefined,
              context: (user && (user.accountType === 'mentor' || user.role === 'mentor')) ? 'mentor' : 'student',
              mentorId: (user && (user.accountType === 'mentor' || user.role === 'mentor')) ? user.uid : null,
              className: classNameFromState || undefined,
              section: sectionFromState || undefined,
              artistName: artistNameFromState || undefined
            }
          }, true); // isSaved = true to actually save to dashboard
          
          console.log('✅ Reimagine activity with feedback saved to dashboard:', savedActivity);
          console.log('✅ Appreciation feedback is grouped with reimagine activity (not separate)');
        } catch (activityError) {
          console.error('❌ Failed to save reimagine activity with feedback:', activityError);
        }
      } else {
        setChatHistory(prev => [
          ...prev,
          { role: 'ai', text: 'Sorry, I could not generate art appreciation for that response.' }
        ]);
      }
    } catch (err) {
      console.error('🔴 Impact API error:', err);
      // If backend content moderation triggers, err.message contains the
      // kid‑friendly message from getContentModerationMessage('reimagine appreciation', ...)
      if (err.message && err.message.toLowerCase().includes('artmosphere norms')) {
        setContentModerationMessage(err.message);
        setContentModerationKeywords([]);
        setShowContentModerationModal(true);
      } else {
        setChatHistory(prev => [
          ...prev,
          { role: 'ai', text: 'An error occurred while generating art appreciation.' }
        ]);
      }
    }
    setImpactInput("");
    setAppreciationLoading(false);
  }

  return (
    <div
      className={`flex flex-col relative bg-[#FFF8E1] ${
        hideChrome
          ? 'min-h-0 h-auto w-full overflow-x-hidden overflow-y-visible px-0'
          : 'h-screen overflow-hidden px-0 md:px-[2.5cm]'
      }`}
    >
      {/* Amber color splashes background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-amber-200/25 rounded-full blur-3xl" />
        <div className="absolute bottom-40 left-10 w-96 h-96 bg-amber-300/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 right-1/4 w-56 h-56 bg-amber-400/22 rounded-full blur-3xl" />
        <div className="absolute top-1/3 left-1/3 w-40 h-40 bg-amber-200/15 rounded-full blur-2xl" />
        <div className="absolute bottom-1/4 right-1/3 w-48 h-48 bg-amber-300/18 rounded-full blur-3xl" />
      </div>
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-5">
        <img src={splashImage} alt="" className="absolute top-10 right-5 opacity-20" style={{ width: '400px', height: '200px', objectFit: 'cover', transform: 'rotate(15deg)', mixBlendMode: 'multiply', filter: 'blur(2px)' }} />
        <img src={splashImage} alt="" className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-15" style={{ width: '300px', height: '150px', objectFit: 'cover', transform: 'translate(-50%, -50%) rotate(10deg)', mixBlendMode: 'multiply', filter: 'blur(3px)' }} />
      </div>
      <KidBackground variant="blobs" intensity={1} />

      {/* Window — full bleed on mobile */}
      <div
        className={
          hideChrome
            ? 'relative z-10 w-full flex flex-col py-0'
            : 'relative z-10 w-full max-w-full flex-1 min-h-0 flex flex-col py-0 md:py-6 lg:py-8'
        }
      >
        <div
          className={`bg-white/95 rounded-none md:rounded-[32px] shadow-2xl border-0 md:border-2 border-amber-300 flex flex-col ${
            hideChrome
              ? 'min-h-0 overflow-visible px-3 sm:px-5 md:px-7 py-4 md:py-6'
              : 'flex-1 min-h-0 overflow-hidden px-3 sm:px-4 md:px-6 py-3 md:py-5'
          }`}
        >
          {/* Compact header — activity dashboard style */}
          {!hideChrome && (
            <header className="flex-shrink-0 bg-white/95 backdrop-blur-sm border-b border-amber-200/50 shadow-sm max-h-[72px] md:max-h-[80px]">
              <div className="flex items-center justify-between py-2 md:py-2.5 h-full min-h-[52px]">
                <p className="text-sm md:text-base font-semibold text-gray-800 truncate pr-2">Re-imagine your artwork</p>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button type="button" onClick={() => navigate('/my-art-world')} className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all flex items-center justify-center" title="Artist Corner">Artist corner</button>
                  <button type="button" onClick={() => navigate((user && (user.accountType === 'mentor' || user.role === 'mentor')) ? '/mentor-activity-dashboard' : '/activity-dashboard?view=magic-studio', { state: { view: (user && (user.accountType === 'mentor' || user.role === 'mentor')) ? 'mentor' : 'magic-studio' } })} className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all flex items-center justify-center">{(user && (user.accountType === 'mentor' || user.role === 'mentor')) ? 'Mentor Activities' : 'My Activities'}</button>
                </div>
              </div>
            </header>
          )}
          {/* Content: artwork + right panel — scrollable */}
          <div
            className={`relative max-w-7xl mx-auto w-full flex flex-col lg:flex-row lg:items-start gap-4 md:gap-6 lg:gap-8 ${
              hideChrome
                ? 'p-3 sm:p-5 md:p-7 lg:p-9 min-h-0'
                : 'flex-1 min-h-0 overflow-auto p-3 md:p-6 lg:p-8'
            }`}
          >
          {/* Left: Artwork - appears first on mobile */}
          <div className="w-full shrink-0 lg:w-1/2 flex flex-col items-center order-1 lg:order-1 lg:max-w-[50%]">
            {/* Artwork Section */}
            <div className="flex flex-col items-center w-full">
              <div className="relative w-full max-w-md">
                <div className="w-full max-w-md h-[300px] sm:h-[400px] md:h-[500px] rounded-xl md:rounded-2xl border-2 md:border-4 border-amber-200 shadow-lg overflow-hidden cursor-pointer" onClick={() => setExpanded(true)}>
                  {/* Undo Button Overlay */}
                  <button
                    className={`absolute top-2 left-2 md:top-3 md:left-3 bg-white/80 rounded-full p-1.5 md:p-2 shadow hover:bg-white z-20 flex items-center gap-1 md:gap-2 font-bold text-amber-700 transition-all duration-300 text-xs md:text-sm lg:text-base ${imageHistory.length <= 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (imageHistory.length > 1) {
                        setImageHistory(prev => prev.slice(0, -1));
                      }
                    }}
                    disabled={imageHistory.length <= 1}
                    title="Undo to previous image"
                  >
                    <FaUndo className="w-3 h-3 md:w-4 md:h-4" /> <span className="hidden sm:inline">Undo</span>
                  </button>
                  {/* Artwork Image */}
                  <img
                    src={currentImageUrl}
                    alt="Artwork"
                    className="w-full h-full object-contain"
                    loading="eager"
                    fetchpriority="high"
                    onLoad={() => {
                      // Clear any updating spinner and mark artwork as visible
                      setImageUpdating(false);
                      setArtworkLoaded(true);
                    }}
                  />
                  {/* Soft loading placeholder while main artwork image loads */}
                  {!artworkLoaded && (
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-100 via-amber-50 to-amber-200 animate-pulse z-10" />
                  )}
                  {/* Expand Button Overlay */}
                  <button
                    className="absolute top-2 right-2 md:top-3 md:right-3 bg-white/80 rounded-full p-1.5 md:p-2 shadow hover:bg-white z-20"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpanded(true);
                    }}
                    title="Expand"
                  >
                    <FaExpandArrowsAlt className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                </div>
                <p className="text-xs md:text-sm text-amber-600 mt-2 text-center">Tap artwork to expand</p>
              </div>
            </div>
          </div>

          {/* Right Column: Reimagine Prompt + Appreciation - appears below artwork on mobile */}
          <div className="w-full min-w-0 lg:w-1/2 flex flex-col gap-4 md:gap-6 lg:gap-8 order-2 lg:order-2 lg:max-w-[50%]">
              {/* Reimagine Prompt Input (always at top) */}
              <div className="bg-white/90 backdrop-blur-sm border-2 border-amber-200 rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 flex flex-col items-center">
                <h2
                  className={`text-lg md:text-xl font-bold text-amber-700 mb-2 w-full ${
                    hideChrome ? 'text-center' : 'text-left'
                  }`}
                >
                  Reimagine Your Artwork
                </h2>
                <form onSubmit={handleChatSend} className="flex w-full gap-2 mt-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    placeholder="Describe how you want to reimagine your artwork..."
                    className="flex-1 border border-amber-200 rounded-lg px-3 md:px-4 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-amber-300"
                    disabled={chatLoading}
                  />
                  <button
                    type="submit"
                    className="art-btn-primary px-3 md:px-4 py-2 rounded-lg font-bold transition-all text-sm md:text-base whitespace-nowrap"
                    disabled={chatLoading || !chatInput.trim()}
                  >
                    Reimagine
                  </button>
                </form>
              </div>

              {/* Appreciation Box: Only after new image is received */}
              {showImpactQuestion && (
                <div className="bg-white/90 backdrop-blur-sm border-2 border-amber-200 rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 flex flex-col items-center">
                  <h2 className="text-lg md:text-xl font-bold text-amber-800 mb-2">Art Appreciation</h2>
                  <div className="w-full mb-4 text-amber-900 font-semibold text-sm md:text-base">How has this simple tweak changed the way you feel about this artwork?</div>
                  {!appreciation && !appreciationLoading && (
                    <form onSubmit={handleImpactSubmit} className="flex w-full gap-2">
                      <input
                        type="text"
                        value={impactInput}
                        onChange={e => setImpactInput(e.target.value)}
                        placeholder="Share your feelings about the new artwork..."
                        className="flex-1 border border-amber-200 rounded-lg px-3 md:px-4 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-amber-300"
                        disabled={appreciationLoading}
                      />
                      <button
                        type="submit"
                        className="art-btn-primary px-3 md:px-4 py-2 rounded-lg font-bold transition-all text-sm md:text-base whitespace-nowrap"
                        disabled={appreciationLoading || !impactInput.trim()}
                      >
                        Submit
                      </button>
                    </form>
                  )}
                  {appreciationLoading && <div className="text-amber-700 mt-2 text-sm md:text-base">Let's see what Artmosphere thinks about this change...</div>}
                  {appreciation && (
                    <div className="w-full mt-4 text-sm md:text-base">
                      {(() => {
                        // Normalize both legacy {label, appreciation} and new {title, text, suggestion}
                        const items = (Array.isArray(appreciation) ? appreciation : []).map(it => ({
                          title: it.title || it.label || '',
                          text: it.text || it.appreciation || '',
                          suggestion: it.suggestion || ''
                        }));
                        const intro = items.find(i => (i.title || '').toLowerCase() === 'intro');
                        const impact = items.find(i => (i.title || '').toLowerCase() === 'impact of your change');
                        const sugg = items.find(i => (i.title || '').toLowerCase() === "mentor's suggestion");
                        const suggestionBody = (sugg && (sugg.text || sugg.suggestion || '').trim()) || '';

                        // If we don't find the expected structured titles, fall back to showing all texts
                        const hasStructured =
                          (intro && intro.text) ||
                          (impact && impact.text) ||
                          (sugg && (sugg.text || sugg.suggestion));

                        const cardFace =
                          'rounded-xl border border-amber-200 bg-amber-50/90 p-4 md:p-5 shadow min-h-[14rem] md:min-h-[16rem]';

                        if (hasStructured && suggestionBody) {
                          return (
                            <div className="w-full [perspective:1100px]">
                              <div
                                className="relative w-full min-h-[18rem] md:min-h-[22rem] overflow-visible transition-transform duration-700 ease-out"
                                style={{
                                  transformStyle: 'preserve-3d',
                                  transform: impactExploreFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                                }}
                              >
                                <div
                                  className={`${cardFace} absolute inset-0 w-full overflow-y-auto`}
                                  style={{
                                    backfaceVisibility: 'hidden',
                                    WebkitBackfaceVisibility: 'hidden',
                                    transform: 'rotateY(0deg)',
                                  }}
                                >
                                  {intro && intro.text && (
                                    <div className="text-amber-900 font-semibold mb-3">{intro.text}</div>
                                  )}
                                  {impact && impact.text && (
                                    <div className="mb-4 text-stone-800">{impact.text}</div>
                                  )}
                                  <div className="mt-2 flex flex-col items-stretch sm:items-start gap-2">
                                    <button
                                      type="button"
                                      onClick={() => setImpactExploreFlipped(true)}
                                      className="inline-flex items-center justify-center rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 focus:ring-offset-amber-50 transition-colors"
                                    >
                                      Try next
                                    </button>
                                    <p className="text-xs text-amber-800/75">Opens what you could explore on your next pass.</p>
                                  </div>
                                </div>
                                <div
                                  className={`${cardFace} absolute inset-0 flex w-full flex-col overflow-y-auto`}
                                  style={{
                                    backfaceVisibility: 'hidden',
                                    WebkitBackfaceVisibility: 'hidden',
                                    transform: 'rotateY(180deg)',
                                  }}
                                >
                                  <h3 className="text-base font-bold text-amber-900 mb-2">What to explore next</h3>
                                  <p className="flex-1 text-stone-800 font-medium leading-relaxed">{suggestionBody}</p>
                                  <button
                                    type="button"
                                    onClick={() => setImpactExploreFlipped(false)}
                                    className="mt-4 self-start rounded-lg border-2 border-amber-400 bg-white px-4 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-300 transition-colors"
                                  >
                                    Back to feedback
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div className="bg-amber-50/90 border border-amber-200 rounded-xl p-4 md:p-5 shadow">
                            {intro && intro.text && (
                              <div className="text-amber-900 font-semibold mb-3">{intro.text}</div>
                            )}
                            {impact && impact.text && (
                              <div className="mb-6 text-stone-800">{impact.text}</div>
                            )}
                            {sugg && (sugg.text || sugg.suggestion) && (
                              <div className="mt-4 text-stone-800 font-medium">{sugg.text || sugg.suggestion}</div>
                            )}

                            {!hasStructured && items.length > 0 && (
                              <div className="space-y-3">
                                {items.map((it, idx) => (
                                  <div key={idx} className="text-stone-800">
                                    {it.title && (
                                      <div className="font-semibold text-amber-900 mb-1">{it.title}</div>
                                    )}
                                    {it.text && <div>{it.text}</div>}
                                    {it.suggestion && (
                                      <div className="mt-1 text-stone-700 italic">
                                        <strong>Suggestion:</strong> {it.suggestion}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {expanded && (
        <div 
          className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 md:p-6"
          onClick={() => setExpanded(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000,
            width: '100vw',
            height: '100vh'
          }}
        >
          <button
            className="absolute top-3 right-3 md:top-6 md:right-6 bg-white/90 hover:bg-white rounded-full p-2 md:p-3 shadow-lg z-[10001] transition-all"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(false);
            }}
            title="Close"
          >
            <FaTimes className="w-5 h-5 md:w-7 md:h-7" />
          </button>
          <div 
            className="relative max-w-full max-h-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={currentImageUrl}
              alt="Expanded Artwork"
              className="max-w-full max-h-[90vh] md:max-h-[95vh] rounded-xl md:rounded-2xl border-2 md:border-4 border-amber-200 shadow-2xl object-contain"
              style={{ background: 'white' }}
            />
          </div>
        </div>
      )}

      {/* Centered Loading Overlay for Reimagining */}
      {imageUpdating && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-30">
          <div className="bg-white rounded-xl shadow-lg px-8 py-6 flex flex-col items-center">
            <svg className="animate-spin h-8 w-8 text-amber-500 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
            <div className="text-amber-700 font-bold text-lg">Reimagining your artwork...</div>
          </div>
        </div>
      )}

      {/* Centered Loading Overlay for Art Appreciation (impact thinking) */}
      {appreciationLoading && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-30">
          <div className="bg-white rounded-xl shadow-lg px-8 py-6 flex flex-col items-center">
            <svg className="animate-spin h-8 w-8 text-amber-500 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
            <div className="text-amber-800 font-bold text-lg text-center">
              Artmosphere is thinking about your impact…
            </div>
          </div>
        </div>
      )}

      {/* Content Moderation Modal for prompts & impact text */}
      <ContentModerationModal
        isOpen={showContentModerationModal}
        onClose={() => setShowContentModerationModal(false)}
        message={contentModerationMessage}
        matchedKeywords={contentModerationKeywords}
      />
      <UpgradeQuotaModal
        isOpen={showUpgradeModal}
        onClose={dismissUpgradeModal}
        feature={upgradeModalFeature}
        onUpgrade={() => { dismissUpgradeModal(); navigate('/membership'); }}
      />
    </div>
  );
} 